import {
  AthleteActualWorkoutDomain,
  WorkoutLayerDomain,
} from '@strength/domain';
import {
  HasWorkoutLayer,
  mergeAthleteWorkoutLayers,
  Workout,
  WorkoutExercise,
  WorkoutSet,
  ExerciseMutationData,
  SetMetricMutationData,
  SetMutationData,
} from '@strength/domain-workout';
import { WorkoutLayer } from '@strength/db-schema';
import {
  WorkoutLayerCommand,
  WorkoutLayerIdentity,
} from '../block/mutation-fns/types';
import { v4 } from 'uuid';
import {
  createWorkoutLayerEdit,
  editSetMetricEdits,
} from '../block/mutation-fns/workout-draft';
import { AthleteHasWorkoutLayer, WorkoutMutationContext } from './types';
import {
  editExerciseStatusEdits,
  editSetStatusEdits,
  editsWorkoutStatus,
  selectAlternateExerciseEdits,
} from '../block/mutation-fns/workout-actuals';

export interface CreateActualMutationFnsParams {
  actualLayer?: AthleteHasWorkoutLayer | null;
  blockLayer?: HasWorkoutLayer | null;
  modifierLayer?: HasWorkoutLayer | null;
  athleteLayer: HasWorkoutLayer;
  mutation: {
    mutate: (command: AthleteActualWorkoutDomain.Command) => void;
  };
}

/**
 * Shared utility to create actual workout layer mutation functions
 * Used by both EndSessionDrawer and WorkoutContext
 */
export function createActualMutationFns({
  actualLayer,
  blockLayer,
  modifierLayer,
  athleteLayer,
  mutation,
}: CreateActualMutationFnsParams): ActualWorkoutLayerMutationFns {
  const actualLayers = actualLayer ? [actualLayer] : [];

  const bulkMutate = (
    commands: readonly AthleteActualWorkoutDomain.Command[],
  ) => {
    for (const command of commands) {
      mutation.mutate(command);
    }
  };

  const runCommands = (
    context: WorkoutMutationContext,
    commands: WorkoutLayerCommand[],
  ) => {
    const additionalLayers = {
      athletePublishedWorkoutLayer: athleteLayer.layer,
      blockPublishedWorkoutLayer: blockLayer?.layer,
      athleteModifiedPublishedWorkoutLayer: modifierLayer?.layer,
    };

    runAthleteActualsMutations(context, commands, bulkMutate, additionalLayers);
  };

  return makeActualWorkoutLayerMutationFns(actualLayers, (context, command) =>
    runCommands(context, [command]),
  );
}

export type ActualWorkoutLayerMutationFns = {
  onEditSetMetric: (
    context: WorkoutMutationContext,
    workoutId: string,
    workoutSetId: string,
    data: SetMetricMutationData,
  ) => void;
  onSelectAlternateExercise: (
    context: WorkoutMutationContext,
    workoutId: string,
    alternateGroupId: string,
    selectedExerciseId: string,
  ) => void;
  onExerciseStatusEdit: (
    context: WorkoutMutationContext,
    workoutId: string,
    workoutExerciseId: string,
    sets: WorkoutSet[],
    data: Partial<ExerciseMutationData>,
  ) => void;
  onEditSetStatus: (
    context: WorkoutMutationContext,
    workoutId: string,
    workoutSetId: string,
    exercise: WorkoutExercise,
    data: SetMutationData,
  ) => void;
  onChangeWorkoutStatus: (
    context: WorkoutMutationContext,
    workoutId: string,
    workout: Workout,
    status: 'completed' | 'skipped',
  ) => void;
};

export function runAthleteActualsMutations(
  context: WorkoutMutationContext,
  commands: WorkoutLayerCommand[],
  bulkMutate: (commands: readonly AthleteActualWorkoutDomain.Command[]) => void,
  additionalLayers?: {
    athletePublishedWorkoutLayer?: WorkoutLayer;
    blockPublishedWorkoutLayer?: WorkoutLayer;
    athleteModifiedPublishedWorkoutLayer?: WorkoutLayer;
  },
) {
  const athleteActualWorkoutCommands: AthleteActualWorkoutDomain.Command[] = [];

  for (const command of commands) {
    // AthleteActualWorkoutDomain.Edit spirtitually is a subclass of WorkoutLayerDomain.HasWorkoutLayerEdit, which makes this a safe cast
    const edits = command.edits as AthleteActualWorkoutDomain.Edit[];
    const { exists, workoutId, id } = command.identity;
    const { blockId, profileId, athleteVersion, blockVersion } = context;

    if (!exists) {
      edits.unshift({
        modelName: 'AthleteActualWorkoutLayer',
        modelId: id,
        action: 'create',
        data: {
          layer: WorkoutLayerDomain.defaultLayer(),
        },
      });
    }

    // Find the last non-delete edit to get the final layer state
    // When !exists, edits[0] is the create edit with defaultLayer(), but subsequent edits contain the actual changes
    let athleteActualWorkoutLayer: WorkoutLayer | undefined;
    for (let i = edits.length - 1; i >= 0; i--) {
      const edit = edits[i];
      if (
        edit &&
        edit.action !== 'delete' &&
        'data' in edit &&
        edit.data?.layer
      ) {
        athleteActualWorkoutLayer = edit.data.layer;
        break;
      }
    }
    const workout = mergeAthleteWorkoutLayers(
      additionalLayers?.blockPublishedWorkoutLayer
        ? {
            layer: additionalLayers.blockPublishedWorkoutLayer,
            workoutId,
            id: '',
          }
        : undefined,
      additionalLayers?.athletePublishedWorkoutLayer
        ? {
            layer: additionalLayers.athletePublishedWorkoutLayer,
            workoutId,
            id: '',
          }
        : undefined,
      additionalLayers?.athleteModifiedPublishedWorkoutLayer
        ? {
            layer: additionalLayers.athleteModifiedPublishedWorkoutLayer,
            workoutId,
            id: '',
          }
        : undefined,
      athleteActualWorkoutLayer
        ? {
            layer: athleteActualWorkoutLayer,
            workoutId,
            id: '',
          }
        : undefined,
    );

    athleteActualWorkoutCommands.push({
      module: AthleteActualWorkoutDomain.Module,
      identity: {
        workoutId,
        blockId,
        profileId,
        athleteVersion,
        blockVersion,
      },
      edits,
      workout,
    });
  }

  bulkMutate(athleteActualWorkoutCommands);
}

export function makeActualWorkoutLayerMutationFns(
  workoutLayers: AthleteHasWorkoutLayer[],
  mutateFn: (
    context: WorkoutMutationContext,
    command: WorkoutLayerCommand,
  ) => void,
): ActualWorkoutLayerMutationFns {
  function createMutateFn<FPARAM extends unknown[], MPARAM extends unknown[]>(
    fn: (...params: FPARAM) => WorkoutLayerDomain.WorkoutLayerEdit[],
    includeLayer = false,
  ) {
    return (
      context: WorkoutMutationContext,
      workoutId: string,
      ...params: MPARAM
    ) => {
      const workoutLayersForProfile = workoutLayers.filter(
        (wl) => wl.profileId === context.profileId,
      );
      const { identity, workoutLayer } = findWorkoutLayer(
        workoutLayersForProfile,
        workoutId,
      );
      const fnParams = includeLayer ? [workoutLayer, ...params] : params;
      const edits = fn(...(fnParams as FPARAM));

      if (!edits?.length) return;

      const workoutLayerEdit = createWorkoutLayerEdit(
        identity,
        workoutLayer,
        edits,
      );
      mutateFn(context, workoutLayerEdit);
    };
  }

  return {
    onEditSetMetric: createMutateFn(editSetMetricEdits, true),
    onSelectAlternateExercise: createMutateFn(
      selectAlternateExerciseEdits,
      true,
    ),
    onExerciseStatusEdit: createMutateFn(editExerciseStatusEdits, true),
    onEditSetStatus: createMutateFn(editSetStatusEdits, true),
    onChangeWorkoutStatus: createMutateFn(editsWorkoutStatus, true),
  };
}

function findWorkoutLayer(
  workoutLayers: HasWorkoutLayer[],
  workoutId: string,
): {
  workoutLayer: HasWorkoutLayer | undefined;
  identity: WorkoutLayerIdentity;
} {
  const workoutLayer = workoutLayers.find((m) => m.workoutId === workoutId);

  if (!workoutLayer) {
    return {
      workoutLayer,
      identity: {
        workoutId,
        id: v4(),
        exists: false,
        modelName: 'AthleteActualWorkoutLayer',
      },
    };
  } else {
    return {
      workoutLayer,
      identity: {
        workoutId,
        id: workoutLayer.id,
        exists: true,
        modelName: 'AthleteActualWorkoutLayer',
      },
    };
  }
}
